import { Injectable, inject } from '@angular/core';
import { State, Action, StateContext, Store } from '@ngxs/store';
import {
  ResetMobilePhoneProductState,
  SetNewVoiceProductOnMobileProductAction,
  SetVoiceProductOnMobileProductAction,
  SetExistingVoiceSubscriptionOnMobileProductAction,
  RemoveMobileProductAction,
  SetSelectedMobilePhoneIdAction,
  SetSelectedMobileAgreementIdAction,
  AddMobilePhoneReplacementAction,
  AddNewMobilePhoneAction,
  SetIsValidatingImeiAction,
} from './customer-product-mobilephone.actions';
import { patch, updateItem, removeItem, append } from '@ngxs/store/operators';
import { MobilePhoneProduct } from '../../models/mobilephone-product';
import { StateHelperService } from '@core/services/state-helper.service';
import { ProductStartIndex } from '@shared/models/product.model';
import { ProductClassifications } from '@core/enums/product-classifications';
import {
  AdditionalServiceAddAction,
  ProductAddAction,
  ProductRemoveConfirmAction,
  ResetAdditionalServicesChangesOnExistingSubscriptionAction,
  ResetOfferingOnExistingSubscriptionAction,
  SetMobileProductOnVoiceProductAction,
  SetSelectedStepIndexAction,
  VoiceProductUpdateAction,
} from '../customer-product/customer-product.actions';
import { VoiceProduct } from '@shared/models/voice-product.model';
import { AddVoiceSubscriptionForMobilephoneToFormStateAction } from '../form/form.actions';
import { RemoveShippingItemAction, AddShippingItemAction } from '../logistics/logistics.actions';
import { IconNames } from '@shared/enums/icon-names';
import { MobilePaymentType } from '../../enums/mobile-payment-type';
import { CustomerProductSelectors } from '../customer-product/customer-product.selectors';
import {
  CustomerEquipmentUpgradeAgreement,
  MobileReturnMethod,
} from '../../modules/mobilephone/models/equipment-upgrade-agreement.model';
import { VoiceSubscriptionOffering } from '@sales/modules/subscription/models/offerings-response.model';
import { OfferingSelectors } from '../offering/offering.selectors';
import {
  CreateOrUpdateTradeInVendorSubsidyVoucher,
  SelectTradeInProductAction,
} from '../customer-product-trade-in/customer-product-trade-in.actions';
import { MobilBytteSelectors } from '@app/sales/modules/mobilephone/states/mobilbytte/mobilbytte.selectors';
import {
  ResetRedemptionDetailsAction,
  ResetSelectedMobilBytteOffering,
} from '@app/sales/modules/mobilephone/states/mobilbytte/mobilbytte.actions';
import { SimTypeSupport } from '@app/sales/modules/mobilephone/models/sim-type-support';

export interface CustomerProductsMobilePhoneStateModel {
  products: MobilePhoneProduct[];
  selectedMobilephoneId: string;
  selectedMobileAgreementId: string;
  isValidatingImei: boolean;
}

const initialCustomerProductsMobilePhoneState = {
  products: [],
  selectedMobilephoneId: null,
  selectedMobileAgreementId: null,
  isValidatingImei: false,
};

@State<CustomerProductsMobilePhoneStateModel>({
  name: 'customerProductsMobilephone',
  defaults: initialCustomerProductsMobilePhoneState,
})
@Injectable()
export class CustomerProductMobilePhoneState {
  private stateHelper = inject(StateHelperService);
  private store = inject(Store);

  @Action(AddNewMobilePhoneAction)
  public addNewMobilePhone(
    { setState, dispatch, getState }: StateContext<CustomerProductsMobilePhoneStateModel>,
    {
      mobilePhoneVariant,
      mobilePhoneDetails,
      isManualRegistration,
      newMobileProductId,
      voiceProductToClone,
    }: AddNewMobilePhoneAction
  ) {
    const state = getState();
    const existingVoiceProducts = this.store.selectSnapshot<VoiceProduct[]>(
      CustomerProductSelectors.getExistingVoiceProducts
    );
    const allVoiceProducts = this.store.selectSnapshot<VoiceSubscriptionOffering[]>(OfferingSelectors.getAllOfferings);
    const {
      paymentType,
      newOfferingId,
      existingSubscriptionId,
      isSubscriptionUpgrade,
      subscriptionIdToUpgrade,
      imei,
      isMissingIdentifiers,
      isSoldInShop,
      insuranceOption,
      gtin,
      name,
    } = mobilePhoneDetails;

    const newMobileProduct = new MobilePhoneProduct();
    newMobileProduct.id =
      newMobileProductId || this.stateHelper.getIncrementedProductId(state.products, ProductStartIndex.Mobilephone);
    newMobileProduct.mobilePhoneVariant = mobilePhoneVariant;
    newMobileProduct.sendInPost = !isSoldInShop;
    newMobileProduct.imei = imei;
    newMobileProduct.isMissingIdentifiers = isMissingIdentifiers;
    newMobileProduct.name = name;
    newMobileProduct.isExistingSubscription = false;
    newMobileProduct.paymentType = paymentType;
    newMobileProduct.isManualRegistration = isManualRegistration;
    newMobileProduct.isExternalCellPhone =
      isManualRegistration && !(mobilePhoneVariant?.offeringId && paymentType === MobilePaymentType.EquipmentUpgrade);
    if (!!mobilePhoneDetails.mobilBytteOffering) {
      const insuranceOption = mobilePhoneVariant?.mobilbytteInsuranceOptions
        ?.find(x => x.mobilbytteOfferingId === mobilePhoneDetails.mobilBytteOffering)
        .insuranceOptions.find(x => x.offeringId === mobilePhoneDetails.insuranceOption);
      newMobileProduct.insuranceOption = insuranceOption;

      newMobileProduct.mobilBytteOffering = mobilePhoneDetails.mobilBytteOffering;
    } else {
      newMobileProduct.insuranceOption = mobilePhoneVariant?.insuranceOptions?.find(x => x.offeringId === insuranceOption);
    }

    newMobileProduct.gtin = gtin;

    const isEquipmentUpgradeWithExistingSubscription =
      paymentType === MobilePaymentType.EquipmentUpgrade &&
      existingSubscriptionId &&
      existingVoiceProducts.find(voiceProduct => voiceProduct.id === existingSubscriptionId);

    if (isEquipmentUpgradeWithExistingSubscription) {
      setState(patch({ products: append([newMobileProduct]) }));
      dispatch([
        new SetVoiceProductOnMobileProductAction(newMobileProduct.id, existingSubscriptionId),
        new SetMobileProductOnVoiceProductAction(existingSubscriptionId, newMobileProduct.id),
      ]);
    } else if (
      (paymentType === MobilePaymentType.EquipmentUpgrade || paymentType === MobilePaymentType.PayNowWithSubscription) &&
      newOfferingId
    ) {
      if (
        isSubscriptionUpgrade &&
        subscriptionIdToUpgrade &&
        existingVoiceProducts.find(p => p.id === subscriptionIdToUpgrade)
      ) {
        const existingVoiceProduct = existingVoiceProducts.find(p => p.id === subscriptionIdToUpgrade);
        setState(patch({ products: append([newMobileProduct]) }));

        const newAllowedChangedInfo = existingVoiceProduct.allowedSubscriptionOfferings.find(
          o => o.subscriptionOffering.id === newOfferingId
        );

        this.store
          .dispatch([
            new VoiceProductUpdateAction(
              existingVoiceProduct.id,
              newAllowedChangedInfo.subscriptionOffering,
              newAllowedChangedInfo.changeInfo
            ),
            new ResetAdditionalServicesChangesOnExistingSubscriptionAction(existingVoiceProduct.id),
          ])
          .subscribe(() => {
            dispatch([
              new SetVoiceProductOnMobileProductAction(newMobileProduct.id, existingVoiceProduct.id),
              new SetMobileProductOnVoiceProductAction(existingVoiceProduct.id, newMobileProduct.id),
            ]);
          });
      } else if (!isSubscriptionUpgrade) {
        setState(patch({ products: append([newMobileProduct]) }));
        const newVoiceOffering = allVoiceProducts.find(o => o.id === newOfferingId);
        dispatch(new SetNewVoiceProductOnMobileProductAction(newMobileProduct.id, newVoiceOffering, voiceProductToClone));
      }
    } else if (paymentType === MobilePaymentType.PayNowWithoutSubscription) {
      setState(patch({ products: append([newMobileProduct]) }));
    }

    if (newMobileProduct.sendInPost && newMobileProduct.mobilePhoneVariant) {
      this.store.dispatch(
        new AddShippingItemAction({
          id: newMobileProduct.id,
          name: newMobileProduct.mobilePhoneVariant?.name,
          offeringId: newMobileProduct.mobilePhoneVariant.offeringId,
          productClassification: ProductClassifications.Mobile,
          image: newMobileProduct.mobilePhoneVariant.image ? newMobileProduct.mobilePhoneVariant.image.downloadUri : '',
          imagePillText: newMobileProduct.mobilePhoneVariant.simTypeSupport === SimTypeSupport.Esim ? 'Kun eSIM' : '',
          icon: IconNames.ice_phone,
        })
      );
    }

    const isTriggeredFromMobile = true;
    dispatch([
      new SetSelectedMobilePhoneIdAction(newMobileProduct.id),
      new CreateOrUpdateTradeInVendorSubsidyVoucher(isTriggeredFromMobile),
    ]);
  }

  @Action(AddMobilePhoneReplacementAction)
  public addMobilePhoneReplacement(
    { setState, dispatch, getState }: StateContext<CustomerProductsMobilePhoneStateModel>,
    { mobilePhonetype, mobilePhoneDetails, existingAgreementId, isManualRegistration }: AddMobilePhoneReplacementAction
  ) {
    const existingAgreement = this.store
      .selectSnapshot<CustomerEquipmentUpgradeAgreement[]>(MobilBytteSelectors.existingEquipmentUpgradeAgreements)
      .find(a => a.agreementId === existingAgreementId);

    if (existingAgreement.mobileReturnMethod === MobileReturnMethod.ReturnPackage) {
      dispatch(
        new AddShippingItemAction({
          id: existingAgreementId,
          name: 'Returboks',
          offeringId: null,
          productClassification: ProductClassifications.ReturnBox,
          image: '',
          icon: IconNames.ice_delivery,
        })
      );
    }

    const state = getState();
    const newMobileProductId = this.stateHelper.getIncrementedProductId(state.products, ProductStartIndex.Mobilephone);
    dispatch(
      new AddNewMobilePhoneAction(mobilePhonetype, mobilePhoneDetails, isManualRegistration, newMobileProductId)
    ).subscribe(() => {
      const newMobileProduct = getState().products.find(p => p.id === newMobileProductId);

      setState(
        patch({
          products: updateItem<MobilePhoneProduct>(
            product => product.id === newMobileProduct.id,
            patch<MobilePhoneProduct>({
              existingAgreementId: existingAgreementId,
            })
          ),
        })
      );
    });
  }

  @Action(SetSelectedMobilePhoneIdAction)
  public setSelectedMobilephone(
    { getState, dispatch, patchState }: StateContext<CustomerProductsMobilePhoneStateModel>,
    { mobilePhoneId }: SetSelectedMobilePhoneIdAction
  ) {
    if (mobilePhoneId != null && mobilePhoneId !== getState().selectedMobilephoneId)
      dispatch(new SelectTradeInProductAction(false));

    patchState({ selectedMobilephoneId: mobilePhoneId });
  }

  @Action(SetSelectedMobileAgreementIdAction)
  public setSelectedMobileAgreementId(
    { patchState }: StateContext<CustomerProductsMobilePhoneStateModel>,
    { selectedMobileAgreementId }: SetSelectedMobileAgreementIdAction
  ) {
    patchState({ selectedMobileAgreementId: selectedMobileAgreementId });
  }

  @Action(SetNewVoiceProductOnMobileProductAction)
  public setNewVoiceOfferingOnMobileProduct(
    { dispatch }: StateContext<CustomerProductsMobilePhoneStateModel>,
    { mobileProductId, voiceOffering, voiceProductToClone }: SetNewVoiceProductOnMobileProductAction
  ) {
    const oldVoiceProduct = this.store
      .selectSnapshot<VoiceProduct[]>(CustomerProductSelectors.getAllVoiceProducts)
      .find(p => p.mobileProductId === mobileProductId);
    if (oldVoiceProduct) {
      dispatch(new SetMobileProductOnVoiceProductAction(oldVoiceProduct.id, null));
    }

    return dispatch(new ProductAddAction(voiceProductToClone, mobileProductId)).subscribe(() => {
      let voiceProduct = this.store
        .selectSnapshot<VoiceProduct[]>(CustomerProductSelectors.getAllVoiceProducts)
        .find(p => p.mobileProductId === mobileProductId);
      if (voiceProduct) {
        return dispatch(new VoiceProductUpdateAction(voiceProduct.id, voiceOffering, null)).subscribe(() => {
          if (voiceProductToClone && voiceOffering.id === voiceProductToClone?.offering?.id) {
            voiceProductToClone.additionalServices.forEach(service => {
              this.store.dispatch(new AdditionalServiceAddAction(voiceProduct.id, service));
            });
          }
          voiceProduct = this.store
            .selectSnapshot<VoiceProduct[]>(CustomerProductSelectors.getAllVoiceProducts)
            .find(p => p.mobileProductId === mobileProductId);

          dispatch(new SetSelectedStepIndexAction(1));
          dispatch(new SetVoiceProductOnMobileProductAction(mobileProductId, voiceProduct.id));
          dispatch(new AddVoiceSubscriptionForMobilephoneToFormStateAction(voiceProduct));
        });
      }
    });
  }

  @Action(SetExistingVoiceSubscriptionOnMobileProductAction)
  public setExistingVoiceSubscriptionOnOnMobileProduct(
    { dispatch }: StateContext<CustomerProductsMobilePhoneStateModel>,
    { productId, subscriptionId }: SetExistingVoiceSubscriptionOnMobileProductAction
  ) {
    const allVoiceProducts = this.store.selectSnapshot<VoiceProduct[]>(CustomerProductSelectors.getAllVoiceProducts);
    const existingConnectedVoiceProduct = allVoiceProducts
      .filter(p => p.isExistingSubscription)
      .find(p => p.mobileProductId === productId);
    const newConnectedVoiceProduct = allVoiceProducts
      .filter(p => p.isExistingSubscription)
      .find(p => p.subscription?.id === subscriptionId);

    if (existingConnectedVoiceProduct) {
      dispatch(new SetMobileProductOnVoiceProductAction(existingConnectedVoiceProduct.id, null));
    }
    if (newConnectedVoiceProduct) {
      dispatch([
        new SetMobileProductOnVoiceProductAction(newConnectedVoiceProduct.id, productId),
        new SetVoiceProductOnMobileProductAction(productId, newConnectedVoiceProduct.id),
      ]);
    }
  }

  @Action(SetVoiceProductOnMobileProductAction)
  public setVoiceProductOnMobileProduct(
    { setState }: StateContext<CustomerProductsMobilePhoneStateModel>,
    { productId, voiceProductId }: SetVoiceProductOnMobileProductAction
  ) {
    setState(
      patch<CustomerProductsMobilePhoneStateModel>({
        products: updateItem<MobilePhoneProduct>(
          product => product.id === productId,
          patch<MobilePhoneProduct>({
            voiceProductId: voiceProductId,
          })
        ),
      })
    );
  }

  @Action(RemoveMobileProductAction)
  public removeMobileProduct(
    { setState, getState, dispatch }: StateContext<CustomerProductsMobilePhoneStateModel>,
    { productId }: RemoveMobileProductAction
  ) {
    const state = getState();
    const mobilePhoneProduct = state.products.find(p => p.id === productId);

    setState(
      patch<CustomerProductsMobilePhoneStateModel>({
        products: removeItem(item => item.id === productId),
      })
    );

    const isTriggeredFromMobile = true;
    dispatch(new CreateOrUpdateTradeInVendorSubsidyVoucher(isTriggeredFromMobile));

    if (mobilePhoneProduct?.voiceProductId) {
      const connectedVoiceProduct = this.store
        .selectSnapshot<VoiceProduct[]>(CustomerProductSelectors.getAllVoiceProducts)
        .find(v => v.id === mobilePhoneProduct.voiceProductId);
      if (connectedVoiceProduct) {
        this.store
          .dispatch(new SetMobileProductOnVoiceProductAction(mobilePhoneProduct.voiceProductId, null))
          .subscribe(() => {
            if (connectedVoiceProduct.isExistingSubscription) {
              // reset connected offering
              this.store.dispatch(new ResetOfferingOnExistingSubscriptionAction(mobilePhoneProduct.voiceProductId));
            } else {
              // delete connected offering
              this.store.dispatch(new ProductRemoveConfirmAction(mobilePhoneProduct.voiceProductId));
            }
          });
      }
    }

    // If there is a new mobilbytte bytte connected to the mobile product, and there is an existing mobilbytte agreement
    if (!!mobilePhoneProduct?.mobilBytteOffering && mobilePhoneProduct.existingAgreementId) {
      dispatch(new ResetRedemptionDetailsAction(mobilePhoneProduct.existingAgreementId));
    }

    dispatch(new RemoveShippingItemAction(productId, ProductClassifications.Mobile));

    if (mobilePhoneProduct?.existingAgreementId) {
      dispatch(new RemoveShippingItemAction(mobilePhoneProduct.existingAgreementId, ProductClassifications.ReturnBox));
    }

    if (!!MobilBytteSelectors.mobilBytteOfferingSelected) {
      dispatch(new ResetSelectedMobilBytteOffering());
    }
  }

  @Action(ResetMobilePhoneProductState)
  public resetMobilePhoneProduct({ patchState }: StateContext<ResetMobilePhoneProductState>) {
    patchState(initialCustomerProductsMobilePhoneState);
  }

  @Action(SetIsValidatingImeiAction)
  public setIsValidatingImei(
    { patchState }: StateContext<CustomerProductsMobilePhoneStateModel>,
    { isValidatingImei }: SetIsValidatingImeiAction
  ) {
    patchState({
      isValidatingImei,
    });
  }
}
